import React, { useRef, useLayoutEffect, useState, useEffect } from 'react';
import gsap from 'gsap';

// This component shows the "5 red lights" animation.
// It also waits for the API call (isLoading) to finish before proceeding.
function LightsOutAnimation({ isLoading, onAnimationComplete, apiError, onRetry }) {
  const [animationFinished, setAnimationFinished] = useState(false);
  const containerRef = useRef(null);
  const lightsRef = useRef([]);

  // This effect runs the GSAP animation
  useLayoutEffect(() => {
    // A GSAP Context is the modern, React-friendly way to handle GSAP
    // It scopes our selectors and handles cleanup automatically.
    const ctx = gsap.context(() => {
      const lights = lightsRef.current;
      
      // Set initial state (all lights off)
      gsap.set(lights, { opacity: 0.2, backgroundColor: '#8B0000' }); // Dark red

      // Create the timeline
      const tl = gsap.timeline({
        // When the timeline completes, set our React state
        onComplete: () => setAnimationFinished(true)
      });

      // Animate each light turning on, one by one
      lights.forEach((light, index) => {
        tl.to(light, {
          opacity: 1,
          backgroundColor: '#FF0000', // Bright red
          boxShadow: '0 0 20px 5px #FF0000',
          duration: 0.1,
        }, index * 0.7 + 0.5); // Stagger the start time
      });

      // "Lights Out!" - Turn all lights off
      tl.to(lights, {
        opacity: 0,
        backgroundColor: '#1a1a1a',
        boxShadow: 'none',
        duration: 0.3,
      }, "+=0.5"); // Wait 0.5s after the last light is on

    }, containerRef); // Scope the context to our main element

    // Cleanup function
    return () => ctx.revert();
  }, []); // Empty dependency array means this runs once on mount

  // This effect checks if *both* the animation and the API call are done
  useEffect(() => {
    // If we're not loading, and the animation is done, and there's no error...
    if (!isLoading && animationFinished && !apiError) {
      // ...then we're clear to show the results page.
      onAnimationComplete();
    }
  }, [isLoading, animationFinished, apiError, onAnimationComplete]);

  // Helper to decide what text to show
  const getStatusMessage = () => {
    if (apiError) {
      return (
        <div className="text-center">
          <p className="text-f1-red text-xl mb-6">{apiError}</p>
          <button
            onClick={onRetry}
            className="py-2 px-6 bg-f1-red text-white font-semibold rounded-md hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      );
    }
    if (isLoading) {
      return "Analyzing race data...";
    }
    if (!isLoading && animationFinished) {
      return "Calculations complete!";
    }
    return "It's lights out!";
  };

  return (
    <div ref={containerRef} className="flex flex-col items-center justify-center min-h-[50vh]">
      {/* The 5 Lights */}
      <div className="flex space-x-4 mb-8 bg-f1-gray-dark p-3 rounded-sm">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            ref={el => lightsRef.current[i] = el}
            className="w-16 h-16 md:w-20 md:h-20 bg-f1-gray rounded-full"
            style={{ opacity: 0.2, backgroundColor: '#E2BFBF' }} // Initial state
          />
        ))}
      </div>
      
      {/* Status Message */}
      <p className="text-f1-black text-2xl font-semibold tracking-wider animate-pulse">
        {getStatusMessage()}
      </p>
    </div>
  );
}

export default LightsOutAnimation;